import {
  LinkSection,
  type HubFolderItem,
  type HubItemsReorderRequest,
  type HubLinkItem,
} from "@vellum/vellum-api";

export type HubItem = HubLinkItem | HubFolderItem;

/** A top-level item, as the reorder API and drag state refer to it. */
export type HubItemRef = Pick<HubItem, "kind" | "id">;

// Internal first, then External. The API already orders by section then
// sort_order; the hub decides how the sections stack on the page.
export const SECTION_ORDER: LinkSection[] = [
  LinkSection.INTERNAL,
  LinkSection.EXTERNAL,
];

export function itemsInSection(items: HubItem[], section: LinkSection) {
  return items.filter((item) => item.section === section);
}

export function folders(items: HubItem[]): HubFolderItem[] {
  return items.filter((item): item is HubFolderItem => item.kind === "folder");
}

export function sameItem(item: HubItemRef, ref: HubItemRef) {
  return item.kind === ref.kind && item.id === ref.id;
}

/**
 * Moves the top-level item `dragged` to sit at `targetIndex` among the
 * top-level items of `targetSection`, returning the whole list re-sectioned and
 * re-ordered. Dropping past the end of a section (targetIndex === length)
 * appends. A folder that changes section takes its links with it, as the API's
 * cascade does.
 */
export function withItemMoved(
  items: HubItem[],
  dragged: HubItemRef,
  targetSection: LinkSection,
  targetIndex: number,
): HubItem[] {
  const item = items.find((candidate) => sameItem(candidate, dragged));
  if (!item) {
    return items;
  }

  const moved: HubItem =
    item.kind === "folder"
      ? {
          ...item,
          section: targetSection,
          links: item.links.map((link) => ({
            ...link,
            section: targetSection,
          })),
        }
      : { ...item, section: targetSection };
  const next: HubItem[] = [];

  for (const section of SECTION_ORDER) {
    const remaining = itemsInSection(items, section).filter(
      (candidate) => !sameItem(candidate, dragged),
    );
    if (section === targetSection) {
      remaining.splice(Math.min(targetIndex, remaining.length), 0, moved);
    }
    next.push(
      ...remaining.map((candidate, index) => ({
        ...candidate,
        sort_order: index,
      })),
    );
  }

  return next;
}

/** Moves link `draggedId` to `targetIndex` inside folder `folderId`. */
export function withFolderLinkMoved(
  items: HubItem[],
  folderId: string,
  draggedId: string,
  targetIndex: number,
): HubItem[] {
  return items.map((item) => {
    if (item.kind !== "folder" || item.id !== folderId) {
      return item;
    }
    const dragged = item.links.find((link) => link.id === draggedId);
    if (!dragged) {
      return item;
    }
    const remaining = item.links.filter((link) => link.id !== draggedId);
    remaining.splice(Math.min(targetIndex, remaining.length), 0, dragged);
    return {
      ...item,
      links: remaining.map((link, index) => ({ ...link, sort_order: index })),
    };
  });
}

/**
 * Every section in full, as `PUT /hub/items/order` requires, for a drag in the
 * grid. Folder contents don't change, so no folders are listed.
 */
export function sectionsOrderBody(items: HubItem[]): HubItemsReorderRequest {
  return {
    sections: SECTION_ORDER.map((section) => ({
      section,
      items: itemsInSection(items, section).map(({ kind, id }) => ({
        kind,
        id,
      })),
    })).filter((entry) => entry.items.length > 0),
  };
}

/** One folder's contents in full, for a drag inside that folder. */
export function folderOrderBody(
  items: HubItem[],
  folderId: string,
): HubItemsReorderRequest {
  const folder = folders(items).find((candidate) => candidate.id === folderId);
  return {
    folders: folder
      ? [
          {
            folder_id: folder.id,
            link_ids: folder.links.map((link) => link.id),
          },
        ]
      : [],
  };
}
